import { definedMap, dontAwait } from "@glideapps/ts-necessities";
import { asString, asBoolean, loadedDefinedMapWithDefault } from "common/components/component-helpers";
import { LOADING, ActionSource } from "common/components/types";
import { GlideFunctionalRenderer } from "common/components/uis";
import SwitchView from "../ui/views/switch/switch-view";
import { SwitchVisual } from "../ui/views/switch/switch-view-types";
import { useForce } from "hooks/use-force";
import { useForceValueProducers } from "hooks/use-value-producers";
import { useCallback } from "react";
import { useOnClickFromAction } from "hooks/use-on-click-from-action";
import { useEditComponentEnabledViaNetworkStatus } from "hooks/use-edit-components-enabled-via-network-status";

export const SwitchMaterialRenderer: GlideFunctionalRenderer = p => {
    const { env, getValueProducers, ctx } = p;
    const [force] = useForce();
    const [
        [value, caption, description, onChangeValue, isEnabledValue, visualValue, allowWrappingValue],
        [valueProducer],
    ] = useForceValueProducers(getValueProducers, force, ctx);
    const { onClick: onChangeCB } = useOnClickFromAction(onChangeValue, ActionSource.Switch, ctx, env);
    const [editingAllowed] = useEditComponentEnabledViaNetworkStatus(env);

    const onChange = useCallback(
        (newValue: boolean): void => {
            if (valueProducer === undefined) return;

            valueProducer.set(newValue);
            // FIXME: Why would `ctx` and `env` ever be undefined?
            if (onChangeCB !== undefined) {
                dontAwait(onChangeCB(undefined));
            }
        },
        [onChangeCB, valueProducer]
    );

    if (value === LOADING || caption === LOADING || description === LOADING) {
        return env.renderProgress();
    }
    const isEnabled = editingAllowed && (onChangeCB === undefined || isEnabledValue !== false);
    const visual = loadedDefinedMapWithDefault(visualValue, "switch", asString) as SwitchVisual;
    const allowWrapping = loadedDefinedMapWithDefault(allowWrappingValue, true, asBoolean);

    return (
        <SwitchView
            title={definedMap(caption, asString)}
            description={definedMap(description, asString)}
            checked={asBoolean(value)}
            onChanged={onChange}
            isEnabled={isEnabled}
            visual={visual}
            allowWrapping={allowWrapping}
        />
    );
};
